import { computed, Ref, ref } from 'vue-demi';

import { ResizerType } from '@/types/common';

const getCssValue = (value: number | undefined) => value ? `${value}px` : '';

export function useComponentResize(
  elRef: Ref,
  minWidth: number = 1,
  minHeight: number = 1,
  positionUpdateCallback?: (args: { top?: number; left?: number; }) => void,
) {
  const elementX = ref<number | null>(null);
  const elementY = ref<number | null>(null);
  const elementWidth = ref<number>();
  const elementHeight = ref<number>();
  const currentResizerType = ref<ResizerType | null>();
  const originalMouseX = ref<number>();
  const originalMouseY = ref<number>();
  const originalElWidth = ref<number>();
  const originalElHeight = ref<number>();
  const originalElTop = ref<number>();
  const originalElLeft = ref<number>();
  const isResizing = ref(false);
  const isTopResizeAvailable = ref(true);
  const isLeftResizeAvailable = ref(true);
  const isTopPositioningAvailable = ref(true);
  const isLeftPositioningAvailable = ref(true);
  let container = null as HTMLElement | null;
  const containerBox = ref<DOMRect>();

  const elementStyles = computed(() => {
    const styles = {
      width: getCssValue(elementWidth.value),
      height: getCssValue(elementHeight.value),
    };
    if (elementY.value) {
      Object.assign(styles, { top: getCssValue(elementY.value) });
    }
    if (elementX.value) {
      Object.assign(styles, { left: getCssValue(elementX.value) });
    }
    return styles;
  });

  function initializeResize(getContainer: () => HTMLElement | undefined) {
    container = getContainer() ?? null;
    setContainerBox();

    /* NOTE: if component doesn't fit container, don't use its original sizes,
    but replace them with contaner sizes */
    const width = containerBox.value && elRef.value.clientWidth > containerBox.value.width ?
      containerBox.value.width : elRef.value.clientWidth;
    const height = containerBox.value && elRef.value.clientHeight > containerBox.value.height ?
      containerBox.value.height : elRef.value.clientHeight;
    elementWidth.value = width;
    elementHeight.value = height;
  }

  function getCurrentPosition() {
    const elementStyles = window.getComputedStyle(elRef.value);
    const inset = elementStyles.inset.split(' ');
    const top = inset[0];
    const left = inset[inset.length - 1];
    return {
      left: parseInt(left),
      top: parseInt(top),
    };
  }

  function getWindowBox() {
    const height = window.innerHeight;
    const width = window.innerWidth;
    return {
      top: 0,
      left: 0,
      right: width,
      bottom: height,
      height,
      width,
    } as DOMRect;
  }

  function setContainerBox() {
    if (container) {
      containerBox.value = container.getBoundingClientRect();
    } else {
      containerBox.value = getWindowBox();
    }
  }

  function enableResize(resizer: HTMLElement) {
    resizer.addEventListener('mousedown', startResize);
  }

  function setCurrentResizerType(e: MouseEvent) {
    const dataResizerAttr = (e.target as HTMLElement).getAttribute('data-resizer');
    if (dataResizerAttr) currentResizerType.value = dataResizerAttr as ResizerType;
  }

  function startResize(e: MouseEvent) {
    isResizing.value = true;
    isLeftPositioningAvailable.value = true;
    isTopPositioningAvailable.value = true;
    originalMouseX.value = e.pageX;
    originalMouseY.value = e.pageY;
    originalElWidth.value = elRef.value.clientWidth;
    originalElHeight.value = elRef.value.clientHeight;
    const { left, top } = getCurrentPosition();
    originalElTop.value = top;
    originalElLeft.value = left;
    setCurrentResizerType(e);
    setContainerBox();
    window.addEventListener('mousemove', resizeElement);
    window.addEventListener('mouseup', stopResize);
  }

  function stopResize() {
    if (!isResizing.value) return;
    window.removeEventListener('mousemove', resizeElement);
    if (positionUpdateCallback && elementY.value) {
      positionUpdateCallback({ top: elementY.value });
      elementY.value = null;
    }
    if (positionUpdateCallback && elementX.value) {
      positionUpdateCallback({ left: elementX.value });
      elementX.value = null;
    }
    isResizing.value = false;
    currentResizerType.value = null;
  }

  function updateHeight(pageY: number, direction: 'top' | 'bottom') {
    const diff = pageY - originalMouseY.value!;
    let newHeight = 0;
    if (direction === 'top') {
      newHeight = originalElHeight.value! - diff;
    } else {
      newHeight = originalElHeight.value! + diff;
    }
    if (isTopPositioningAvailable.value) {
      const minHeightCheck = newHeight >= minHeight;
      setContainerBox();
      const containerBottomCheck = pageY < containerBox.value!.bottom;
      if (minHeightCheck && containerBottomCheck) {
        elementHeight.value = newHeight;
        isTopResizeAvailable.value = true;
      } else if (!minHeightCheck) {
        isTopResizeAvailable.value = false;
      }
    }
  }

  function updateWidth(pageX: number, direction: 'left' | 'right') {
    const diff = pageX - originalMouseX.value!;
    let newWidth = 0;
    if (direction === 'right') {
      newWidth = originalElWidth.value! + diff;
    } else {
      newWidth = originalElWidth.value! - diff;
    }
    setContainerBox();
    if (isLeftPositioningAvailable.value) {
      const minWidthCheck = newWidth >= minWidth;
      const containerRightCheck = pageX < containerBox.value!.right;
      if (minWidthCheck && containerRightCheck) {
        elementWidth.value = newWidth;
        isLeftResizeAvailable.value = true;
      } else if (!minWidthCheck) {
        isLeftResizeAvailable.value = false;
      }
    }
  }

  function updateTopPosition(pageY: number) {
    if (isTopResizeAvailable.value) {
      const newTop = originalElTop.value! + (pageY - originalMouseY.value!);
      if (newTop >= 0) {
        elementY.value = newTop;
        isTopPositioningAvailable.value = true;
      } else {
        isTopPositioningAvailable.value = false;
      }
    }
  }

  function updateLeftPosition(pageX: number) {
    if (isLeftResizeAvailable.value) {
      const newLeft = originalElLeft.value! + (pageX - originalMouseX.value!);
      if (newLeft >= 0) {
        elementX.value = newLeft;
        isLeftPositioningAvailable.value = true;
      } else {
        isLeftPositioningAvailable.value = false;
      }
    }
  }

  function resizeElement(e: MouseEvent) {
    switch (currentResizerType.value) {
      case 'right':
        updateWidth(e.pageX, 'right');
        break;
      case 'top':
        updateTopPosition(e.pageY);
        updateHeight(e.pageY, 'top');
        break;
      case 'bottom':
        updateHeight(e.pageY, 'bottom');
        break;
      case 'left':
        updateLeftPosition(e.pageX);
        updateWidth(e.pageX, 'left');
        break;
      case 'bottom-left':
        updateWidth(e.pageX, 'left');
        updateLeftPosition(e.pageX);
        updateHeight(e.pageY, 'bottom');
        break;
      case 'bottom-right':
        updateWidth(e.pageX, 'right');
        updateHeight(e.pageY, 'bottom');
        break;
      case 'top-right':
        updateWidth(e.pageX, 'right');
        updateTopPosition(e.pageY);
        updateHeight(e.pageY, 'top');
        break;
      case 'top-left':
        updateLeftPosition(e.pageX);
        updateWidth(e.pageX, 'left');
        updateTopPosition(e.pageY);
        updateHeight(e.pageY, 'top');
        break;
      default:
        break;
    }
  }

  return {
    enableResize,
    elementStyles,
    initializeResize,
    currentResizerType,
  };

}
